import React, { useRef, useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { fonts } from "../common/mixins";
import { LeftArrow, RightArrow } from "../icons";
import useDevice from "../../hooks/useDevice";
import { BREAKPOINT_RESERVATION_PAGE } from "../../pageComponents/reservationPage/constants/breakpoints";

const CarouselContainer = styled.div<{ $nested?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    ${(props) =>
      !props.$nested &&
      css`
        width: 100vw;
        margin-left: -24px;
        overflow: hidden;
      `}
  }
`;

const CarouselWrapper = styled.div<{ $gap: number; $nested?: boolean }>`
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  gap: ${(props) => props.$gap}px;
  height: 100%;

  /* Hide scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    ${(props) => !props.$nested && css`padding: 0 24px;`}
  }
`;

const CarouselItem = styled.div<{ $itemWidth: string; $gap: number }>`
  display: flex;
  flex: 0 0 ${(props) => (props.$itemWidth ? props.$itemWidth : "273px")};
  scroll-snap-align: start;
  scroll-snap-stop: always;

  @media (min-width: 901px) {
    flex: 0 0
      ${({ $itemWidth, $gap }) =>
        $itemWidth ? $itemWidth : `calc(50% - ${$gap / 2}px)`};
  }
  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    scroll-snap-align: center;
  }
`;

const NavButton = styled.button<{
  $direction: "left" | "right";
  $visible: boolean;
  $nested?: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => `${props.$direction}: -15px;`}

  padding: 0;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Nested carousels sit inside the card, where the options popup
     (products/ProductCard.tsx OptionsPopup, z-index: 5) spans the full card
     width — an arrow pinned above it would paint over the popup once the
     arrows move inside the box, so nested arrows sit below that layer. */
  z-index: ${(props) => (props.$nested ? 1 : 10)};
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};

  --btn-stroke: #d8dbe0;
  --btn-fill: white;

  &:hover {
    --btn-stroke: #a8abaf;
  }

  &:active {
    --btn-stroke: #a8abaf;
    --btn-fill: #f9fbfc;
  }
`;

const Counter = styled.div`
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  ${fonts.normalTextBold()};
  z-index: 10;
`;

interface CarouselProps {
  children: React.ReactNode[];
  itemWidth?: number; // Width of each item
  gap?: number; // Gap between items in pixels (default: 16)
  scrollBy?: number; // Number of items to scroll (default: 2)
  showCounter?: boolean; // Show counter component (default: false)
  nested?: boolean; // opts out of the page-level full-bleed + outside-arrow behaviour (default: false)
  label?: { previous: string; next: string }; // nav button aria-labels (default: English)
}

export function Carousel({
  children,
  itemWidth,
  gap = 16,
  scrollBy = 2,
  showCounter = false,
  nested = false,
  label = { previous: "Previous", next: "Next" },
}: CarouselProps) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const { isTouchDevice } = useDevice();
  const showNavigation = children.length > 1 && !isTouchDevice;

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    // Intersection Observer to track first and last visible items, and current index
    const observer = new IntersectionObserver(
      (entries) => {
        let firstVisibleIndex = Infinity;

        entries.forEach((entry) => {
          const index = itemsRef.current.indexOf(
            entry.target as HTMLDivElement,
          );

          if (index === 0) {
            setShowLeftButton(!entry.isIntersecting);
          }

          if (index === children.length - 1) {
            setShowRightButton(!entry.isIntersecting);
          }

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            firstVisibleIndex = Math.min(firstVisibleIndex, index);
          }
        });

        if (firstVisibleIndex !== Infinity) {
          setCurrentIndex(firstVisibleIndex);
        }
      },
      {
        root: carousel,
        threshold: 0.5,
      },
    );

    // Observe all items
    itemsRef.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    return () => observer.disconnect();
  }, [children.length]);

  const scrollNext = () => {
    const nextIndex = Math.min(currentIndex + scrollBy, children.length - 1);
    itemsRef.current[nextIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  };

  const scrollPrev = () => {
    const prevIndex = Math.max(currentIndex - scrollBy, 0);
    itemsRef.current[prevIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  };

  return (
    <CarouselContainer $nested={nested}>
      {showNavigation && (
        <NavButton
          id="left-button"
          type="button"
          $direction="left"
          $visible={showLeftButton}
          $nested={nested}
          onClick={scrollPrev}
          aria-label={label.previous}
        >
          <LeftArrow />
        </NavButton>
      )}

      <CarouselWrapper ref={carouselRef} $gap={gap} $nested={nested}>
        {children.map((child, index) => (
          <CarouselItem
            key={index}
            ref={(el) => (itemsRef.current[index] = el)}
            $itemWidth={itemWidth ? ` ${itemWidth}px` : null}
            $gap={gap}
          >
            {child}
          </CarouselItem>
        ))}
      </CarouselWrapper>

      {showNavigation && showCounter && (
        <Counter>
          {currentIndex + 1} / {children.length}
        </Counter>
      )}

      {showNavigation && (
        <NavButton
          id="right-button"
          type="button"
          $direction="right"
          $visible={showRightButton}
          $nested={nested}
          onClick={scrollNext}
          aria-label={label.next}
        >
          <RightArrow />
        </NavButton>
      )}
    </CarouselContainer>
  );
}
